import { type AllProfileWorkOpportunityResultsListView_userAccount$key } from '#app/__generated__/relay/AllProfileWorkOpportunityResultsListView_userAccount.graphql.js';
import { type AllProfileWorkOpportunityResultsListViewQuery as AllProfileWorkOpportunityResultsListViewQueryType } from '#app/__generated__/relay/AllProfileWorkOpportunityResultsListViewQuery.graphql.js';
import { CardListItemAnimation } from '#app/components/CardListItemAnimation/CardListItemAnimation.js';
import { JobPostingCard } from '#app/components/JobPostingCard/JobPostingCard.js';
import { WorkOpportunitySkeleton } from '#app/components/SkeletonLoaders/WorkOpportunitySkeleton.js';
import { getFilteredOpportunities } from '#app/features/opportunities/utils.js';
import { BlankCardMessage } from '#app/features/profile/components/BlankCardMessage/BlankCardMessage.js';
import { CreateCard } from '#app/features/profile/components/CreateCard/CreateCard.js';
import { usePaginatedInfiniteLoad } from '#app/hooks/usePaginatedInfiniteLoad.js';
import { type ThemeWithMultiColumnLayoutMode } from '#app/types/multiColumnLayoutMode.js';
import { formatName } from '#app/utilities/formatName.js';
import { graphql, usePaginationFragment } from '@contra/relay';
import { useCallback } from 'react';
import { css, styled } from 'styled-components';

const CardsListContainer = styled.div<{
  theme: ThemeWithMultiColumnLayoutMode;
}>`
  ${({ theme }) => css`
    & > * + * {
      margin-top: 32px;

      ${theme.mediaQueries.lg} {
        margin-top: 40px;
      }
    }
  `}
`;

const JobPostingCardWrapper = styled(CardListItemAnimation)`
  ${({ theme }) => css`
    & > a {
      padding: 0;
      margin-bottom: 32px;

      ${theme.mediaQueries.lg} {
        margin-bottom: 40px;
      }
    }
  `}
`;

const Divider = styled.hr`
  ${({ theme }) => css`
    background-color: ${theme.colorsV2.gray30};
    height: 1px;
    border: 0;
  `}
`;

export const AllProfileWorkOpportunityResultsListView = ({
  firstName,
  jobPostingsRef,
  visitorCanEdit,
}: {
  readonly firstName: null | string;
  readonly jobPostingsRef: AllProfileWorkOpportunityResultsListView_userAccount$key;
  readonly visitorCanEdit: boolean;
}) => {
  const { data, hasNext, isLoadingNext, loadNext } = usePaginationFragment<
    AllProfileWorkOpportunityResultsListViewQueryType,
    AllProfileWorkOpportunityResultsListView_userAccount$key
  >(
    graphql`
      fragment AllProfileWorkOpportunityResultsListView_userAccount on UserProfile
      @argumentDefinitions(
        first: { type: "Int", defaultValue: 5 }
        after: { type: "String" }
      )
      @refetchable(queryName: "AllProfileWorkOpportunityResultsListViewQuery") {
        id
        jobPostings(first: $first, after: $after)
          @connection(
            key: "AllProfileWorkOpportunityResultsListView_jobPostings"
          ) {
          edges {
            cursor
            node {
              id
              visitorCanEdit
              status
              visitorApplication {
                paidProjectV2 {
                  status
                }
              }
              ...JobPostingCard_jobPosting
            }
          }
        }
      }
    `,
    jobPostingsRef,
  );

  const loadMore = useCallback(() => {
    loadNext(5);
  }, [loadNext]);

  const loadMoreElementRef = usePaginatedInfiniteLoad<HTMLDivElement>(
    loadMore,
    hasNext,
    isLoadingNext,
    { rootMargin: '200px', threshold: 0 },
  );

  const filteredData = getFilteredOpportunities(data?.jobPostings.edges);

  const hasJobPostings = filteredData?.[0];

  if (!hasJobPostings && visitorCanEdit) {
    return <CreateCard variant="opportunity" />;
  }

  return (
    <>
      <CardsListContainer>
        {visitorCanEdit ? <CreateCard variant="opportunity" /> : null}
        {hasJobPostings ? (
          filteredData.map(({ node }, positionIndex) => (
            <JobPostingCardWrapper
              isFirstElement={positionIndex === 0}
              key={node.id}
            >
              <JobPostingCard
                hideActions
                jobPostingRef={node}
                positionIndex={positionIndex + 1}
                visitorFilterType="ALL"
              />
              {positionIndex === filteredData.length - 1 ? null : <Divider />}
            </JobPostingCardWrapper>
          ))
        ) : (
          <BlankCardMessage>
            {formatName({ firstName }, 'firstName')} hasn't posted a job yet
          </BlankCardMessage>
        )}
      </CardsListContainer>

      <WorkOpportunitySkeleton
        isLoading={isLoadingNext}
        ref={loadMoreElementRef}
      />
    </>
  );
};
